/**
 * Session hooks for API state hydration (P0-3)
 *
 * These hooks connect the frontend to the persisted session state in the backend,
 * enabling page refresh, session sharing, and real-time progress tracking.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { queryKeys } from '@/api/queryKeys';
import {
  getSessionDetails,
  getSessionStatus,
  createSession,
  updateResponseAction,
  listSessions,
  deleteSession,
} from '@/api/endpoints';
import { useSessionStore } from '@/stores/useSessionStore';
import type {
  SessionDetail,
  SessionProgress,
  CreateSessionRequest,
  UpdateResponseActionRequest,
  DdqSessionQuestion,
  SessionStatus,
} from '@/types/api.types';

/**
 * Hook to load session details from API and hydrate the store
 */
export function useSessionLoader(sessionId: string | null) {
  const { setCurrentSession, setQuestions, setCurrentSessionId } = useSessionStore();

  const query = useQuery({
    queryKey: queryKeys.sessionDetails(sessionId || ''),
    queryFn: () => getSessionDetails(sessionId!),
    enabled: !!sessionId,
    staleTime: 30_000, // Consider fresh for 30 seconds
  });

  // Hydrate store when data loads
  useEffect(() => {
    if (query.data) {
      const session = query.data;

      // Convert API session to store format
      setCurrentSessionId(session.session_id);
      setCurrentSession({
        id: session.session_id,
        fileName: session.uploaded_filename,
        totalQuestions: session.question_count,
        processedQuestions: session.responses.length,
        exactMatches: session.tier_breakdown?.exact ?? 0,
        suggestedMatches: session.tier_breakdown?.suggested ?? 0,
        generatedResponses: session.tier_breakdown?.generated ?? 0,
        noMatches: session.tier_breakdown?.no_match ?? 0,
        automationRate: session.automation_rate ?? 0,
        status: mapSessionStatus(session.status),
        created: session.created_at,
        error: session.error_message ?? undefined,
      });

      // Convert API responses to store question format
      const questions: DdqSessionQuestion[] = session.responses.map((r) => ({
        index: r.question_index,
        result_id: null,
        question_id: r.response_id,
        incoming_question: r.question_text,
        response_tier: r.response_tier,
        confidence: r.confidence * 100, // API returns 0-1, frontend expects 0-100
        suggested_response: r.matched_answer ?? '',
        citations: r.citations ?? [],
        requires_review: r.requires_review,
        review_reason: r.review_reason,
        canonical_question_id: null,
        source_qualitative_id: null,
        userAction: mapUserAction(r.user_action),
        userResponse: r.modified_answer ?? undefined,
        modifiedBy: r.reviewed_by ?? undefined,
        modifiedDate: r.reviewed_at ?? undefined,
      }));

      setQuestions(questions);
    }
  }, [query.data, setCurrentSession, setQuestions, setCurrentSessionId]);

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    session: query.data,
  };
}

/**
 * Hook to poll session status during background processing
 */
export function useSessionStatus(
  sessionId: string | null,
  options?: {
    enabled?: boolean;
    onReady?: (session: SessionProgress) => void;
    onError?: (error: Error) => void;
  }
) {
  // Store callbacks in refs to avoid dependency instability from inline arrow functions
  const onReadyRef = useRef(options?.onReady);
  const onErrorRef = useRef(options?.onError);

  useEffect(() => {
    onReadyRef.current = options?.onReady;
    onErrorRef.current = options?.onError;
  }, [options?.onReady, options?.onError]);

  // Safety valve: stop polling after 5 minutes to prevent infinite loops
  const pollStartRef = useRef<number | null>(null);
  const MAX_POLL_DURATION_MS = 5 * 60 * 1000;

  const query = useQuery({
    queryKey: queryKeys.sessionStatus(sessionId || ''),
    queryFn: () => getSessionStatus(sessionId!),
    enabled: !!sessionId && (options?.enabled ?? true),
    refetchOnWindowFocus: false,
    retry: 3,
    refetchInterval: (query) => {
      // Stop polling on repeated fetch failures
      if (query.state.fetchFailureCount > 3) return false;

      // Stop polling after max duration
      if (pollStartRef.current === null) {
        pollStartRef.current = Date.now();
      } else if (Date.now() - pollStartRef.current > MAX_POLL_DURATION_MS) {
        return false;
      }

      // Poll every 2 seconds while processing, stop when done
      // TanStack Query v5: callback receives query object, not data directly
      const sessionStatus = query.state.data?.status;
      if (!sessionStatus) return 2000;
      if (sessionStatus === 'processing' || sessionStatus === 'pending') {
        return 2000;
      }
      return false;
    },
  });

  // Callbacks for status changes — deps use primitive status to avoid re-firing
  const status = query.data?.status ?? null;
  useEffect(() => {
    if (status === 'ready' && query.data && onReadyRef.current) {
      onReadyRef.current(query.data);
    }
    if (status === 'failed' && onErrorRef.current) {
      onErrorRef.current(new Error(query.data?.error_message ?? 'Processing failed'));
    }
  }, [status, query.data]);

  return {
    status,
    progress: query.data,
    isPolling: status === 'processing' || status === 'pending',
    isReady: status === 'ready',
    isFailed: status === 'failed',
  };
}

/**
 * Hook to create a new session with background processing
 */
export function useCreateSession() {
  const queryClient = useQueryClient();
  const { setCurrentSessionId } = useSessionStore();

  return useMutation({
    mutationFn: (request: CreateSessionRequest) => createSession(request),
    onSuccess: (data) => {
      setCurrentSessionId(data.session_id);
      // Invalidate sessions list to include new session
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions() });
    },
  });
}

/**
 * Hook to update a response action (Accept/Modify/Reject) with optimistic updates
 */
export function useUpdateResponseAction(sessionId: string) {
  const queryClient = useQueryClient();
  const { updateQuestion } = useSessionStore();

  return useMutation({
    mutationFn: ({
      responseId,
      request,
    }: {
      responseId: string;
      request: UpdateResponseActionRequest;
    }) => updateResponseAction(sessionId, responseId, request),

    // Optimistic update
    onMutate: async ({ responseId, request }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.sessionDetails(sessionId) });

      // Snapshot previous value
      const previousSession = queryClient.getQueryData<SessionDetail>(
        queryKeys.sessionDetails(sessionId)
      );

      // Optimistically update store
      updateQuestion(responseId, {
        userAction: mapUserAction(request.action),
        userResponse: request.modified_answer ?? undefined,
      });

      return { previousSession };
    },

    // Rollback on error
    onError: (_err, { responseId }, context) => {
      if (context?.previousSession) {
        const response = context.previousSession.responses.find(
          (r) => r.response_id === responseId
        );
        if (response) {
          updateQuestion(responseId, {
            userAction: mapUserAction(response.user_action),
            userResponse: response.modified_answer ?? undefined,
          });
        }
      }
    },

    // Refetch after success or error
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessionDetails(sessionId) });
    },
  });
}

/**
 * Hook to list sessions with filtering
 */
export function useSessionsList(params?: {
  status?: string;
  limit?: number;
  offset?: number;
  firm_id?: number;
}) {
  return useQuery({
    queryKey: queryKeys.sessionsList(params),
    queryFn: () => listSessions(params),
    staleTime: 60_000, // Consider fresh for 1 minute
    refetchInterval: (query) => {
      // Poll while any session is still being processed so statuses flip to
      // Ready on the list without a manual reload; otherwise don't poll.
      const hasActive = query.state.data?.sessions?.some(
        (s) => s.status === 'processing' || s.status === 'pending'
      );
      return hasActive ? 5_000 : false;
    },
  });
}

/**
 * Hook to delete a session
 */
export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => deleteSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions() });
    },
  });
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function mapSessionStatus(status: SessionStatus): 'Pending' | 'Processing' | 'Ready' | 'Error' {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'processing':
      return 'Processing';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Error';
    default:
      return 'Pending';
  }
}

function mapUserAction(
  action: string | null
): 'Pending' | 'Accepted' | 'Modified' | 'Rejected' | undefined {
  if (!action) return 'Pending';
  switch (action) {
    case 'Accept':
      return 'Accepted';
    case 'Modify':
      return 'Modified';
    case 'Reject':
      return 'Rejected';
    default:
      return 'Pending';
  }
}
